import { createMemo, createResource, createSignal, onCleanup, onMount, type Resource } from 'solid-js';
import Cart from '@brytdesigns/shopify-ajax-cart';

export type SellingPlanAdjustment = {
  value_type: 'percentage' | 'price' | 'fixed_amount';
  value: number;
  position: number;
  order_count: number | null;
};

export type SellingPlan = {
  id: number;
  name: string;
  recurring_deliveries: boolean;
  price_adjustments: SellingPlanAdjustment[];
};

export type SellingPlanGroup = {
  selling_plans: SellingPlan[];
};

export type RecommendedProduct = {
  id: number;
  title: string;
  url: string;
  price: number;
  price_min: number;
  price_max: number;
  price_varies: boolean;
  compare_at_price: number | null;
  featured_image: string;
  variants: { id: number }[];
  available: boolean;
  tags: string[];
  selling_plan_groups?: SellingPlanGroup[];
};

const recsCache = new Map<number, RecommendedProduct[]>();

export const fetchRecommendationsForProduct = async (
  baseUrl: string,
  productId: number,
  limit: number,
  intent: string,
): Promise<RecommendedProduct[]> => {
  if (recsCache.has(productId)) return recsCache.get(productId)!;

  const url = `${baseUrl}.json?product_id=${productId}&limit=${limit}&intent=${intent}`;
  const response = await fetch(url);
  if (!response.ok) return [];

  try {
    const data = await response.json();
    const products: RecommendedProduct[] = data.products || [];
    recsCache.set(productId, products);
    return products;
  } catch {
    return [];
  }
};

export { formatMoney } from '@/lib/utils/money';

export const computeAutoshipPrice = (product: RecommendedProduct): number | null => {
  const groups = product.selling_plan_groups;
  if (!groups?.length) return null;

  const group = groups.find((g) => g.selling_plans?.some((p) => p.recurring_deliveries));
  const plan = group?.selling_plans?.find((p) => p.recurring_deliveries);
  if (!plan) return null;

  const adjustment = plan.price_adjustments?.[0];
  if (!adjustment) return product.price;

  switch (adjustment.value_type) {
    case 'percentage':
      return Math.round(product.price * (1 - adjustment.value / 100));
    case 'fixed_amount':
      return Math.max(0, product.price - adjustment.value);
    case 'price':
      return adjustment.value;
    default:
      return product.price;
  }
};

export const computeSavePercent = (product: RecommendedProduct): number | null => {
  if (!product.compare_at_price || product.compare_at_price <= product.price) {
    return null;
  }
  return Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100);
};

export type UseCartRecommendationsOptions = {
  element: HTMLElement;
  baseUrl: string;
  cartUrl: string;
  limit: number;
  intent: string;
};

export type UseCartRecommendationsResult = {
  recommendations: () => RecommendedProduct[];
  isLoading: () => boolean;
  hasItems: () => boolean;
  resource: Resource<RecommendedProduct[] | undefined>;
};

export const useCartRecommendations = (opts: UseCartRecommendationsOptions): UseCartRecommendationsResult => {
  const [isInView, setIsInView] = createSignal(false);

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    },
    { rootMargin: '200px' },
  );

  onMount(() => observer.observe(opts.element));
  onCleanup(() => observer.disconnect());

  const cartItemProductIds = createMemo(() => {
    if (!isInView()) return null;
    const items = Cart.data?.items || [];
    return items.map((item) => Number(item.product_id));
  });

  const [resource] = createResource(cartItemProductIds, async (productIds) => {
    if (!productIds || productIds.length === 0) return [];

    const seenIds = new Set<number>();
    const combined: RecommendedProduct[] = [];

    for (const id of productIds) {
      const recs = await fetchRecommendationsForProduct(opts.baseUrl, id, opts.limit, opts.intent);
      for (const rec of recs) {
        if (!seenIds.has(rec.id)) {
          seenIds.add(rec.id);
          combined.push(rec);
        }
      }
      if (combined.length >= opts.limit) break;
    }

    return combined;
  });

  const recommendations = createMemo(() => {
    const raw = resource() || [];
    const cartIds = new Set(cartItemProductIds() || []);
    return raw.filter((rec) => !cartIds.has(rec.id)).slice(0, opts.limit);
  });

  const isLoading = () => resource.loading;
  const hasItems = () => isInView() && (isLoading() || recommendations().length > 0);

  return { recommendations, isLoading, hasItems, resource };
};
